import styled from '@emotion/styled';
import type { ReactNode } from 'react';

/**
 * メインエリア（v2）。ppfw-client の `Template` の
 * `#main-area` / `#title-area` / `#view-area` に対応する。
 *
 * 見た目と構造の双方が変わる：
 * - 寸法は CSS（flex）で決めるため、`calcMainAreaWidth()` や `calcViewAreaHeight()` に相当する
 *   JS の測定実装を持たない。
 * - スクロール対象は `#view-area` だけで、上部は外に出す。
 * - `sticky` と `z-index` を使わず、flex 列の構成で重ね順を解決する。
 */

/**
 * メインエリアの root。`<main>` 要素として描画。
 *
 * 枠線も影も持たない。pp_bos_onboarding_front の `Template` の `main` に合わせたもので、
 * あちらはメインエリアを card として立てず、ヘッダー・サイドバーで区切った残り全面を
 * 地色のまま使う。
 *
 * 周りの余白（`pt: 0.5` / `px: 2`）はあちらと同じく内側に持つ。外側（{@link MainAreaSlotV2}）に
 * 空けると地色の帯ができてメインエリアが card として浮いて見えるため。中の各領域は左右の
 * 詰めを持たず、この余白に収まる。上部領域の区切り線が左右の端まで伸びず内側で止まるのも
 * これによる。
 */
const MainAreaRoot = styled.main`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: var(--ps-space-inline) var(--ps-space-inset-loose) 0;
  background-color: var(--ps-color-background-surface);
`;

/**
 * 上部領域。画面名とページ内ナビゲーション。
 *
 * 下端の区切り線は、pp_bos_onboarding_front で画面名を出している帯（`GradientBar` の
 * breadcrumb 行）の下端の線に対応する。あちらは画面名を帯に、見出し（`h2`）を中身の側に
 * 別々に持つが、v2 は画面名をこの領域の `h1` に統合しているので、線もこちらに置く。
 */
const TitleAreaRoot = styled.header`
  display: flex;
  flex-direction: column;
  gap: var(--ps-space-inline);
  flex-shrink: 0;
  padding: var(--ps-space-inset) 0;
  background-color: var(--ps-color-background-surface);
  border-bottom: var(--ps-border-width-divider) solid var(--ps-color-border-default);
`;

/**
 * 画面名。左端のアクセント線は文字の行の高さに揃えて出す（見出し領域全体には伸ばさない）。
 *
 * アクセント線の太さ 4px と 20px / 700 の字送りは、他画面の見出し
 * （pp_bos_onboarding_front の `h2`）に揃えた実寸。太さの ramp（border-width の各段）にも
 * 書体の役割（text の各役割。subheading は 18px/600、heading は 28px/700）にも無い値なので
 * ここに直接持つ。
 */
const TitleHeading = styled.h1`
  margin: 0;
  padding-left: var(--ps-space-inset);
  border-left: 4px solid var(--ps-color-text-heading);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ps-color-text-heading);
`;

/**
 * 中身のスクロール領域。縦にはみ出した分はここだけがスクロールする。
 *
 * 上下の詰めだけを持つ。左右は {@link MainAreaRoot} の余白が担う。16px は
 * pp_bos_onboarding_front の見出し（`h2` の `my: 2`）が中身の側に空けている間隔に揃えたもので、
 * 上端の分は {@link TitleAreaRoot} の区切り線と中身を離す役割も兼ねる。
 */
const ViewAreaRoot = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: var(--ps-space-inset-loose) 0;
  box-sizing: border-box;
`;

interface MainAreaV2Props {
  /** 画面名。`h1` として出す。 */
  title: string;
  /** 画面の中身。縦にはみ出した分はここだけがスクロールする。 */
  children?: ReactNode;
}

/**
 * メインエリア。上に画面名、中に中身（スクロール可）を置く。
 *
 * - `title` は必須。
 * - 寸法（高さと幅）は置く側（`MainAreaSlotV2`）から与える。ヘッダーが
 *   占める高さを知っているのは shell の側なので、こちらで持つと黙って食い違う。
 *
 * `BusinessShellV2` の中でだけ使う。design tokens の宣言はあちらが注入しているので、外に
 * 置くと `var(--ps-*)` がすべて未定義になり、色も余白も無い状態で描画される（`var()` に
 * fallback を書いていないため、例外も警告も出ない）。
 */
export function MainAreaV2({ title, children }: MainAreaV2Props) {
  return (
    <MainAreaRoot>
      <TitleAreaRoot id="page-top">
        <TitleHeading>{title}</TitleHeading>
      </TitleAreaRoot>
      <ViewAreaRoot>{children}</ViewAreaRoot>
    </MainAreaRoot>
  );
}
